import classNames from 'classnames';
import Link from 'next/link';
import React from 'react';
import { VscLoading as IconLoading } from 'react-icons/vsc';

type ButtonBaseProps = {
  as?: 'button' | 'link';
  variant?: 'primary' | 'secondary' | 'white' | 'blue' | 'gray' | 'gray-light';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type ButtonAsButtonProps = ButtonBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    as?: 'button';
  };

type ButtonAsLinkProps = ButtonBaseProps &
  Omit<React.ComponentProps<typeof Link>, keyof ButtonBaseProps> & {
    as: 'link';
  };

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const Button = (props: ButtonProps) => {
  const { as = 'button', children, variant, className, fullWidth, size = 'md', loading = false, disabled, ...otherProps } = props;

  const buttonClassNames = classNames(
    'w-full uppercase',
    {
      'tablet:w-fit': !fullWidth,
    },
    'text-center transition-all duration-200 hover:shadow-xl',
    'font-bold',
    {
      'text-sm rounded-lg px-1.5 py-1.5 ': size === 'sm',
      'text-sm rounded-xl px-1.5 py-1.5 mobile:px-10 ': size === 'md',
      'text-lg': size === 'lg',
    },
    loading ? 'cursor-not-allowed' : 'cursor-pointer',
    loading ? 'bg-gray-300 text-gray-500' : 'hover:bg-opacity-90',
    loading && 'flex items-center  justify-center',
    // The hover text color repeats the resting one on purpose (SCRUM-2424): the trial LPs set
    // `:where(.root) a:hover { color: var(--link-hover) }`, which outranks a plain `text-*` only on hover
    // and painted the link variant's label green on the green primary background.
    variant === 'primary' && 'bg-green-300 text-black hover:text-black',
    variant === 'secondary' && 'bg-blue-300 text-white hover:text-white',
    variant === 'gray' && 'bg-gray-300 text-white hover:text-white',
    variant === 'gray-light' && 'bg-[#879BAB] text-[#D6E4EF] hover:text-[#D6E4EF]',
    variant === 'white' && 'bg-white text-black hover:text-black',
    variant === 'blue' && 'bg-[#065BFF] text-[#CCDDFF] hover:text-[#CCDDFF]',
    className
  );

  const content = (
    <>
      {loading && <IconLoading aria-hidden='true' focusable='false' className='me-2 size-5 animate-spin' />}
      {children}
    </>
  );

  if (as === 'link') {
    const { onClick, ...linkProps } = otherProps as React.ComponentProps<typeof Link>;

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (loading || disabled) {
        event.preventDefault();
        return;
      }

      onClick?.(event);
    };

    return (
      <Link {...linkProps} className={buttonClassNames} aria-disabled={loading || disabled} aria-busy={loading || undefined} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  const { type = 'button', ...buttonProps } = otherProps as React.ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <button {...buttonProps} type={type} className={buttonClassNames} disabled={loading || disabled} aria-busy={loading || undefined}>
      {content}
    </button>
  );
};

export default Button;
